import React, { useState } from "react";
import logo from "src/assets/img/logo.svg";
import { Link } from "react-router-dom";
import Password from "src/assets/icons/Password";
import Email from "src/assets/icons/Email";
import { PATH_AUTH } from "src/routes/paths";
import { FormProvider, RHFTextField } from "src/components/Reusable/hook-form";
import { useSignupForm } from "src/hooks";
import { Lock } from "lucide-react";
import loginImg from 'src/assets/img/login-background.webp';
import laptopMockup from 'src/assets/img/contract-vault-laptop.webp';
import { SpinnerIcon } from "src/assets/icons/Spinner";
import { Button } from "src/components/ui/Button";
import { Checkbox } from "src/components/ui/Checkbox";
import { cn } from "src/utils/Functions";
import EnvironmentSwitcher from "src/components/EnvironmentSwitcher";
import { isNonProdEnvironment } from "src/constants/api.constants";

const Signup = () => {
  // Use the custom hook for all business logic
  const { 
    methods,
    onSubmit,
    emailToVerify,
    isLoading,
    showVerification,
    setShowVerification,
    verificationCode,
    errorMessage,
    setErrorMessage,
    handleVerificationChange,
    handleVerification,
    handleResendVerification
  } = useSignupForm();

  // UI-only state
  const [showEnvironmentSwitcher, setShowEnvironmentSwitcher] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Watch termsAccepted from form state
  const termsAccepted = methods.watch('termsAccepted');

  return (
    <main className="">
      <div className="login-page">
        <header className="fixed flex justify-between items-center top-0 left-0 right-0 z-50 p-8">
          {isNonProdEnvironment() && (
            <Button
              className="fixed top-8 right-8"
              onClick={() => setShowEnvironmentSwitcher(true)}>
              Environment
            </Button>
          )}
        </header>
        <section className="min-h-screen flex">
          {/* background  */}
          <div 
            className="fixed inset-0 bg-cover bg-center bg-no-repeat z-[-1] h-full"
            style={{
              backgroundImage: `url(${loginImg})`,
              backgroundSize: 'cover'
            }}
          >
             <div className="absolute inset-0 z-[1] bg-black/85" />
          </div>
         
          {/* Left side - hidden on mobile/tablet */}
          <div className="hidden lg:grid flex-[50%] place-items-center px-16 py-16">
            <div className="w-full h-full bg-white/5 rounded-[40px] p-12 overflow-hidden backdrop-blur-lg flex flex-col">
              {/* EIQ Logo */}
              <div className="mb-8">
                <img src={logo} alt="EIQ" className="w-16" />
              </div>
              
              {/* Laptop Mockup */}
              <div className="flex-1 w-full flex justify-center items-center">
                <img className="w-full max-w-4xl ml-[calc(50%)]" src={laptopMockup} alt="Contract Vault Dashboard" />
              </div>
              
              {/* Hero Text */}
              <div className="space-y-6 mt-8">
                <h1 className="text-xl font-bold text-white leading-tight">
                Your AI Deal Orchestrator
                </h1>
                <p className="text-md text-white/80 leading-relaxed max-w-lg">
                 Work with your team to evaluate, sign, and track deals with AI-powered clarity. From negotiation to payments, orchestrate everything in one secure platform.
                </p>
              </div>
            </div>
          </div>
          
          {/* Right side - responsive */}
          <div className="w-full lg:flex-[50%] px-4 py-6 sm:px-8 lg:px-16 grid place-items-center">
            <div className="flex flex-col gap-2 w-full max-w-md lg:w-[90%]">
              <h4 className="text-lg font-medium text-white">
                Sign Up 
              </h4>
              <p className="text-white/80 font-medium mb-6">Create your account</p>
                
              {!showVerification ? (
                <FormProvider methods={methods} onSubmit={onSubmit}>
                  <div className="flex flex-col gap-4">
                    <div className="">
                      <label htmlFor="signupCode" className="block text-white font-medium mb-2">Signup Code</label> 
                      <div className="relative w-full">
                        <div className="w-fit pointer-events-none absolute top-1/2 -translate-y-1/2 left-4 z-10">
                          <Lock className="size-4 text-white/60" />
                        </div>
                        <RHFTextField
                          className="pl-8"
                          name="signupCode"
                          type="text"
                          placeholder="Enter signup code"
                        />
                      </div>
                      <small className="text-white/60 text-sm mt-1 block">
                        Contact us to get your signup code
                      </small>
                    </div>
                    
                    <div className="">
                      <label htmlFor="email" className="block text-white font-medium mb-2">Email</label> 
                      <div className="relative w-full">
                        <div className="w-fit pointer-events-none absolute top-1/2 -translate-y-1/2 left-4 z-10">
                          <Email className="size-4" />
                        </div>
                        <RHFTextField
                          className="pl-8"
                          name="email"
                          type="email"
                          placeholder="your.email@example.com"
                        />
                      </div>
                    </div>
                    
                    <div className="">
                      <label htmlFor="password" className="block text-white font-medium mb-2">Create Password</label>
                      <div className="relative w-full">
                        <button aria-label="Toggle password visibility" className="w-fit absolute top-1/2 -translate-y-1/2 left-4" type="button" onClick={() => setShowPassword(!showPassword)}>
                          <Password className="size-4" />
                        </button>
                        <RHFTextField
                          className="pl-8"
                          name="password"
                          type={showPassword ? "text" : "password"}
                          placeholder="*******"
                        />
                      </div>
                      <small className="text-white/60 text-sm mt-1 block">
                        Password must contain at least 8 characters, including uppercase, lowercase, number, and special character.
                      </small>
                    </div>
                    
                    <div className="">
                      <label htmlFor="confirmPassword" className="block text-white font-medium mb-2">Confirm Password</label>
                      <div className="relative w-full">
                        <button aria-label="Toggle confirm password visibility" className="w-fit absolute top-1/2 -translate-y-1/2 left-4" type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)}>
                          <Password className="size-4" />
                        </button>
                        <RHFTextField
                          className="pl-8"
                          name="confirmPassword"
                          type={showConfirmPassword ? "text" : "password"}
                          placeholder="*******"
                        />
                      </div>
                    </div>
                    
                    <div className="flex items-center text-white">
                      <Checkbox
                        checked={termsAccepted}
                        id="termsAccepted"
                        name="termsAccepted"
                        onCheckedChange={(checked) => methods.setValue('termsAccepted', checked === true)}
                      />
                      <label className="pl-4" htmlFor="termsAccepted">
                        I agree to the{" "}
                        <Link className="text-white/80 underline" to="/terms">terms and conditions</Link>
                      </label>
                    </div>
                    
                    <Button 
                      className={cn("rounded-full py-4", {"bg-white text-black": isLoading})} 
                      type="submit" 
                      disabled={isLoading || !termsAccepted}
                      variant="default"
                      size="lg"
                    >
                      {isLoading ? <SpinnerIcon className="!w-8 !h-8 text-black/60 animate-spin" /> : "Create Account"}
                    </Button>
                    
                    <p className="text-white mx-auto">
                      Already have an account? <Link className="text-white/80" to={PATH_AUTH.login}>Log in</Link>
                    </p>
                  </div>
                </FormProvider>
              ) : (
                <form className="flex flex-col gap-8" onSubmit={handleVerification}>
                  <div className="space-y-4">
                    <h5 className="text-lg font-medium text-white">Verify Your Account</h5>
                    <p className="text-white/80">A verification code has been sent to <strong className="text-white">{emailToVerify}</strong>. Please check your email and enter the code below.</p>
                  </div>
                  
                  <div className="">
                    <label htmlFor="verificationCode" className="block text-white font-medium mb-2">Verification Code</label>
                    <input
                      className="w-full px-6 py-4 bg-white/10 border border-white/20 rounded-2xl text-white placeholder:text-white/50 focus:outline-none focus:ring-2 focus:ring-white/30 focus:border-transparent"
                      type="text"
                      name="verificationCode"
                      id="verificationCode"
                      placeholder="Enter verification code"
                      value={verificationCode}
                      onChange={handleVerificationChange}
                      autoFocus
                    />
                    {errorMessage && <span className="text-red-400 text-sm mt-2 block">{errorMessage}</span>}
                  </div>
                  
                  <div className="flex flex-col gap-8">
                    <Button 
                      className={cn("rounded-full py-4", {"bg-white text-black": isLoading})} 
                      type="submit" 
                      disabled={isLoading}
                      variant="default"
                      size="lg"
                    >
                      {isLoading ? <SpinnerIcon className="!w-8 !h-8 text-black/60 animate-spin" /> : "Verify Account"}
                    </Button>
                    
                    <Button
                      className="rounded-full text-white py-4"
                      type="button"
                      onClick={handleResendVerification}
                      disabled={isLoading}
                      variant="outline"
                      size="lg"
                    >
                      Resend Code
                    </Button>
                  </div>
                  
                  <p className="text-white mx-auto">
                    <Link
                      className="text-white/80"
                      to="#"
                      onClick={(e) => {
                        e.preventDefault();
                        setShowVerification(false);
                        setErrorMessage("");
                      }}
                    >
                      Back to Signup
                    </Link>
                  </p>
                </form>
              )}
              </div>
          </div>
        </section>
      </div>
      
      <EnvironmentSwitcher 
        open={showEnvironmentSwitcher} 
        onClose={() => setShowEnvironmentSwitcher(false)} 
      />
    </main>
  );
};

export default Signup;